import Axios, {
  AxiosPromise,
  AxiosRequestConfig,
  Method,
  ResponseType
} from "axios";
import config from "@/config";
import { User } from "../models/User";
import router from "@/router";

import SecureLS from "secure-ls";
/**
 * Payload type for user session credentials
 */
type CreatePayload = {
  email: string;
  password: string;
};
/**
 * Response type for a new user session
 */
type IndexCreateResponse = {
  session: {
    token: string;
    user: User;
  };
};
/**
 * Singleton class for a user session
 */
class Session {
  private endpoint = config.hostname + "/sessions/";

  private _user: User | null = null;
  private _token: string | null = null;

  securels!: SecureLS;

  constructor() {
    this.securels = new SecureLS({
      encryptionSecret: "98ahsf3whwe",
      encodingType: "AES"
    });
    // Temporary user from localstorage
    let user = this.securels.get("user");
    if (user) {
      this._user = user as User;
    }
    // Token from localstorage
    this._token = this.securels.get("token") as string;
    // Fetch updated user
    if (this._token) {
      this.update();
    }
  }
  /**
   * Returns the session user
   */
  getUser(): User | null {
    return this._user;
  }
  /**
   * Sets the session user
   *
   * @param user
   */
  setUser(user: User | null) {
    this._user = user;
  }
  /**
   * Returns the session JWT
   */
  getToken() {
    return this._token;
  }
  /**
   * Sets the session JWT
   *
   * @param token
   */
  setToken(token: string | null) {
    this._token = token;
  }
  /**
   * Fetches an updated session object
   */
  private me = (): AxiosPromise<IndexCreateResponse> => {
    return this.requestWithAuth("get", this.endpoint);
  };
  /**
   * Updates the session user
   */
  update = () => {
    this.me()
      .then(response => {
        this._user = response.data.session.user;
        this._token = response.data.session.token;
      })
      .catch(err => {
        this.logout();
      });
  };
  /**
   * Requests a new session object from the API using the provided credentials
   *
   * @param {CreatePayload} credentials
   */
  login = (credentials: CreatePayload): AxiosPromise<IndexCreateResponse> => {
    return Axios.post(this.endpoint, credentials);
  };
  /**
   * Deletes the stored session and redirects to login page
   */
  logout = () => {
    this.securels.remove("token");
    this.securels.remove("user");
    this._user = null;
    this._token = null;
    router.push("/login");
  };

  /**
   * Resets the user password associated to the provided email
   */
  resetPassword = (email: string) => {
    return Axios.post(this.endpoint + "reset_password", { email: email });
  };

  /**
   * Resets the user password associated to the provided email
   */
  changePassword = (password: string) => {
    return this.requestWithAuth(
      "post",
      this.endpoint + "change_password",
      undefined,
      { password: password }
    );
  };

  /**
   * Resets the user password associated to the provided email
   */
  validateUser = (user_id: number) => {
    return this.requestWithAuth("post", this.endpoint + "validate", undefined, {
      user_id: user_id
    });
  };

  /**
   * Returns an AxiosRequest with Authorization headers using JWT if available
   *
   * @param {string} method
   * @param {string} url
   * @param {object} params
   */
  requestWithAuth = (
    method: Method,
    url: string,
    params?: object,
    data?: object,
    responseType?: ResponseType
  ) => {
    const config: AxiosRequestConfig = {
      method,
      url,
      params,
      data,
      responseType
    };
    // Adds the Authorization headers if a session is active
    if (this._token)
      config.headers = { Authorization: "Bearer " + this._token };
    return Axios.request(config);
  };
}

let session = new Session();

export default session;
